import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Tooltip } from '@mui/material';
import InsertCommentIcon from '@mui/icons-material/InsertComment';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { creators as KPIActions } from '../../../Reducers/Reporting/KPI';
import { creators as ReportSettingActions } from '../../../Reducers/Reporting/ReportSetting';
import { enMonths } from '../../../Assets/js/constant';
import { financialMonth } from '../../../Utils/Functions';
import appdata from '../../../Services/appdata';
import TopChart from './TopChart';
import BottomChart from './BottomChart';

const KPI = ({
  defaultYear,
  defaultMonth,
  defaultDimDate,
  defaultStartMonth,
  selectedCompany,
  kpiSetting,
  bubbleFlag
}) => {
  const dispatch = useDispatch();
  const kpiSummary = useSelector((state) => state.Reporting.Summary.reportingSummaries?.kpi ?? '');
  const summaryData = useSelector((state) => state.Reporting.KPI.summaryData);
  const lastFetchedCompany = useSelector(
    (state) => state?.Reporting?.Default?.defaultCompany ?? null,
  );
  const lastFetchedDimDate = useSelector(
    (state) => state?.Reporting?.Default?.defaultDimDate ?? null,
  );

  const [resize, setResize] = useState(false);
  const [selectedYears, setSelectedYears] = useState([]);
  const [selectedTopItems, setSelectedTopItems] = useState([]);
  const [selectedTopLabels, setSelectedTopLabels] = useState([]);
  const [selectedRightLabels, setSelectedRightLabels] = useState([]);
  const [selectedLeftItems, setSelectedLeftItems] = useState([]);
  const [selectedRightItems, setSelectedRightItems] = useState([]);
  const [choice, setChoice] = useState('Group');

  const onResize = useCallback(() => {
    setResize((prev) => !prev);
  }, []);

  const handleFilter = useCallback(async (event) => {
    if (event.selectedYears) setSelectedYears(event.selectedYears);
    if (event.selectedTopItems) setSelectedTopItems(event.selectedTopItems);
    if (event.selectedTopLabels) setSelectedTopLabels(event.selectedTopLabels);
    if (event.selectedLeftItems) setSelectedLeftItems(event.selectedLeftItems);
    if (event.selectedRightLabels) setSelectedRightLabels(event.selectedRightLabels);    
    if (event.selectedRightItems) setSelectedRightItems(event.selectedRightItems);
    if (event.choice) setChoice(event.choice);

    if (event.choice) {
      await dispatch(
        ReportSettingActions.reportSettingUpdateRequest({
          Kpi: JSON.stringify({
            choice: event.choice || choice,
          }),
        })
      );
    }
  }, [choice, dispatch]);

  useEffect(() => {
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [onResize]);

  useEffect(() => {
    const initializeState = async () => {
      setSelectedYears([defaultYear]);
      setSelectedTopItems(
        appdata.getLocalItem('kpiSelectedTopItems') || []
      );
      setSelectedTopLabels(
        appdata.getLocalItem('kpiSelectedTopLabels') || []
      );
      setSelectedRightLabels(
        appdata.getLocalItem('kpiSelectedRightLabels') ||
        [enMonths[financialMonth(defaultMonth, defaultStartMonth) - 1]]
      );
      setSelectedLeftItems(
        appdata.getLocalItem('kpiSelectedLeftItems') || []
      );
      setSelectedRightItems(
        appdata.getLocalItem('kpiSelectedRightItems') || [
          financialMonth(defaultMonth, defaultStartMonth) - 1,
        ]
      );

      if (kpiSetting) {
        setChoice(JSON.parse(kpiSetting).choice);
      } else {
        await dispatch(
          ReportSettingActions.reportSettingUpdateRequest({
            Kpi: JSON.stringify({
              choice: 'Group',
            }),
          })
        );
      }
    };

    initializeState();
  }, []);

  const triggerKpiRequest = useCallback(() => {
    dispatch(KPIActions.kpiSummaryRequest([defaultYear]));
  }, [dispatch, defaultYear]);

  useEffect(() => {
    const missingDefaults = !lastFetchedCompany || !lastFetchedDimDate;
    if (missingDefaults) {
      triggerKpiRequest();
      return;
    }

    const valuesChanged =
      lastFetchedCompany !== selectedCompany ||
      lastFetchedDimDate !== defaultDimDate;

    if (valuesChanged) {
      triggerKpiRequest();
    }
  }, [defaultYear, defaultDimDate, selectedCompany, lastFetchedCompany, lastFetchedDimDate, triggerKpiRequest]);

  useEffect(() => {
    const updateState = async () => {
      setSelectedYears([defaultYear]);
      setSelectedTopItems([]);
      setSelectedTopLabels([]);
      setSelectedRightLabels([
        enMonths[financialMonth(defaultMonth, defaultStartMonth) - 1]
      ]);
      setSelectedLeftItems([]);
      setSelectedRightItems([
        financialMonth(defaultMonth, defaultStartMonth) - 1,
      ]);      
    };

    updateState();
  }, [defaultYear, defaultMonth, defaultStartMonth]);
  
  return (
    <div style={{ height: '89vh' }}>
      <div>
        <TopChart
          summaryData={summaryData}
          choice={choice}
          selectedTopLabels={selectedTopLabels}
          selectedTopItems={selectedTopItems}
          selectedLeftItems={selectedLeftItems}
          selectedRightItems={selectedRightItems}
          updateFilter={handleFilter}
        />

        <BottomChart
          summaryData={summaryData}
          choice={choice}
          selectedTopItems={selectedTopItems}
          selectedLeftItems={selectedLeftItems}
          selectedRightLabels={selectedRightLabels}
          selectedRightItems={selectedRightItems}
          defaultStartMonth={defaultStartMonth}
          defaultMonth={defaultMonth}
          selectedYears={selectedYears}
          updateFilter={handleFilter}
        />
      </div>

      {bubbleFlag && kpiSummary && (
        <div
          className="reporting-tooltip"
          style={{
            position: 'fixed',
            bottom: 60,
            right: 30,
            zIndex: 1000,
            opacity: 0.5,
            transition: 'opacity 0.3s ease',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => e.target.style.opacity = '1'}
          onMouseLeave={(e) => e.target.style.opacity = '0.5'}
        >
          <Tooltip 
            title={kpiSummary} 
            position="top-end"
            componentsProps={{
              tooltip: { 
                sx: {
                  bgcolor: '#2c3e50',
                  color: '#ffffff',
                  fontSize: '16px',
                  fontWeight: '500',
                  maxWidth: '400px',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }
              }
            }}
          >
            <InsertCommentIcon color="success" />
          </Tooltip>
        </div>
      )}
    </div>
  );
};

KPI.propTypes = {
  defaultYear: PropTypes.number.isRequired,
  defaultMonth: PropTypes.number.isRequired,
  defaultDimDate: PropTypes.string.isRequired,
  defaultStartMonth: PropTypes.number.isRequired,
  selectedCompany: PropTypes.number.isRequired,
  kpiSetting: PropTypes.string.isRequired,
  bubbleFlag: PropTypes.bool.isRequired,
};

export default KPI;
